import { CardType } from "../../../types";
import { isCardPresent, isEmpty } from "../../../utils";
import {
  QuickRankRenderableDestinations,
  QuickRankStateOptimizedMode,
  OptimizedModeMatrix,
  QuickRankActionHandler,
  QuickRankNonRenderableDestinations,
  QuickRankGameResults,
} from "../quickrank";

const countResultsRank = (results: QuickRankGameResults, matrix: OptimizedModeMatrix) => {
  const { currentLayer } = matrix;
  const newResults = { ...results };

  currentLayer.flat().forEach((card, index) => {
    if (card) {
      const { attribute } = card;
      const newAttributeResult = newResults[attribute.id];
      newAttributeResult.count = -1;
      newAttributeResult.rank = index + 1;
      newResults[attribute.id] = newAttributeResult;
    }
  });

  return newResults;
};

const isOptmizedGameFinished = (state: QuickRankStateOptimizedMode): QuickRankStateOptimizedMode => {
  const {
    status: { optimizedModeMatrix },
    cards: { [QuickRankNonRenderableDestinations.TO_RATE]: toRate },
    results,
  } = state;

  const isGameFinished = optimizedModeMatrix.currentLayer.length === 1;
  const newToRate = [...toRate];
  const newRated = [];
  let newResults = { ...results };

  if (isGameFinished && !isEmpty(toRate)) {
    const card = newToRate.shift();
    card && newRated.push(card);
  }

  if (isGameFinished && isEmpty(newToRate)) {
    newResults = countResultsRank(results, optimizedModeMatrix);
  }

  return {
    ...state,
    status: {
      ...state.status,
      isGameFinished,
    },
    cards: {
      ...state.cards,
      [QuickRankNonRenderableDestinations.TO_RATE]: newToRate,
      [QuickRankRenderableDestinations.RATED]: newRated,
    },
    results: newResults,
  };
};

const getCurrentlyMergedArrays = (matrix: OptimizedModeMatrix) => {
  const {
    checkedIndexes: [firstIndex, secondIndex],
    currentLayer,
    nextLayer,
  } = matrix;

  const firstArray = currentLayer[firstIndex];
  const secondArray = currentLayer[secondIndex];
  const destinationArray = nextLayer[Math.ceil(firstIndex / 2)];

  return {
    firstArray: firstArray ? [...firstArray] : undefined,
    secondArray: secondArray ? [...secondArray] : undefined,
    destinationArray: destinationArray ? [...destinationArray] : undefined,
  };
};

const switchOptimizedMatrixLayers = (state: QuickRankStateOptimizedMode): QuickRankStateOptimizedMode => {
  const {
    status: { optimizedModeMatrix },
  } = state;

  const {
    checkedIndexes: [firstIndex, secondIndex],
    currentLayer,
    nextLayer,
  } = optimizedModeMatrix;

  const { firstArray, secondArray } = getCurrentlyMergedArrays(optimizedModeMatrix);

  let newCurrentLayer = [...currentLayer];
  let newNextLayer = [...nextLayer];
  let newFirstIndex = firstIndex;
  let newSecondIndex = secondIndex;

  if (!firstArray || !secondArray) {
    newCurrentLayer = [...nextLayer];
    newNextLayer = Array.from({ length: Math.ceil(newCurrentLayer.length / 2) }, () => []);
    newFirstIndex = 0;
    newSecondIndex = 1;
  }

  if (firstArray && !secondArray) {
    newCurrentLayer[newCurrentLayer.length - 1] = firstArray;
  }

  return {
    ...state,
    status: {
      ...state.status,
      optimizedModeMatrix: {
        checkedIndexes: [newFirstIndex, newSecondIndex],
        currentLayer: newCurrentLayer,
        nextLayer: newNextLayer,
      },
    },
  };
};

const pushArrayToNextLayer = (
  state: QuickRankStateOptimizedMode,
  array: (CardType | null)[],
): QuickRankStateOptimizedMode => {
  const {
    status: { optimizedModeMatrix },
  } = state;

  const {
    checkedIndexes: [firstIndex, secondIndex],
    currentLayer,
    nextLayer,
  } = optimizedModeMatrix;
  const currentDestinationIndex = Math.ceil(firstIndex / 2);

  const { destinationArray = [] } = getCurrentlyMergedArrays(optimizedModeMatrix);

  const newCurrentLayer = [...currentLayer];
  newCurrentLayer[firstIndex] = newCurrentLayer[firstIndex].map(() => null);
  newCurrentLayer[secondIndex] = newCurrentLayer[secondIndex].map(() => null);
  const newDestinationArray = [...destinationArray, ...array.filter((card) => card !== null)];
  const newNextLayer = [...nextLayer];
  newNextLayer[currentDestinationIndex] = newDestinationArray;

  return {
    ...state,
    status: {
      ...state.status,
      optimizedModeMatrix: {
        checkedIndexes: [firstIndex, secondIndex],
        currentLayer: newCurrentLayer,
        nextLayer: newNextLayer,
      },
    },
  };
};

const updateCardStacks = (state: QuickRankStateOptimizedMode, selectedCard?: CardType): QuickRankStateOptimizedMode => {
  const {
    cards: { [QuickRankNonRenderableDestinations.TO_RATE]: toRate },
    status: { optimizedModeMatrix },
    configuration: { showRatingScale },
  } = state;

  const shouldRateCard = showRatingScale && selectedCard && isCardPresent(selectedCard, toRate);

  const { nextLayer, currentLayer } = optimizedModeMatrix;

  const { firstArray = [], secondArray = [] } = getCurrentlyMergedArrays(optimizedModeMatrix);
  const firstComparedCard = firstArray.find((card) => card !== null) ?? null;
  const secondComparedCard = secondArray.find((card) => card !== null) ?? null;

  const flattedNextLayer = nextLayer.flat();

  const newDeck = currentLayer.flat().map((card) => (card ? card : flattedNextLayer.shift() ?? null));
  const newHand = shouldRateCard ? [null, selectedCard] : [firstComparedCard, secondComparedCard];
  const newRated = shouldRateCard ? [selectedCard] : [];

  return {
    ...state,
    cards: {
      ...state.cards,
      [QuickRankRenderableDestinations.DECK]: newDeck,
      [QuickRankRenderableDestinations.HAND]: newHand,
      [QuickRankRenderableDestinations.RATED]: newRated,
    },
  };
};

const nextRound = (state: QuickRankStateOptimizedMode): QuickRankStateOptimizedMode => {
  let newState = isOptmizedGameFinished(state);

  if (newState.status.isGameFinished) {
    return newState;
  }

  const {
    status: { optimizedModeMatrix },
  } = newState;

  if (!optimizedModeMatrix) {
    return newState;
  }

  const {
    checkedIndexes: [firstIndex, secondIndex],
  } = optimizedModeMatrix;

  const { firstArray = [], secondArray = [] } = getCurrentlyMergedArrays(optimizedModeMatrix);

  const firstComparedCard = firstArray.find((card) => card !== null);
  const secondComparedCard = secondArray.find((card) => card !== null);

  if (firstComparedCard && secondComparedCard) {
    return updateCardStacks({
      ...newState,
      status: {
        ...newState.status,
        shouldChangeRound: false,
        isGameFinished: false,
        round: newState.status.round + 1,
        optimizedModeMatrix: optimizedModeMatrix,
      },
    });
  }

  newState = pushArrayToNextLayer(newState, isEmpty(firstArray) ? secondArray : firstArray);
  newState.status.optimizedModeMatrix.checkedIndexes = [firstIndex + 2, secondIndex + 2];
  newState = switchOptimizedMatrixLayers(newState);

  return nextRound(newState);
};

const selectCard = (state: QuickRankStateOptimizedMode, selectedCard: CardType) => {
  const {
    status: { optimizedModeMatrix },
    cards: { [QuickRankRenderableDestinations.HAND]: hand },
    configuration: { showRatingScale },
  } = state;

  const { checkedIndexes, currentLayer, nextLayer } = optimizedModeMatrix;

  const selectedCardHandIndex = hand.findIndex((card) => card === selectedCard);

  if (selectedCardHandIndex === -1) {
    throw new Error("Selected card is not in hand");
  }

  const selectedCardArrayIndex = checkedIndexes[selectedCardHandIndex];
  const selectedCardArray = currentLayer[selectedCardArrayIndex];
  const destinationIndex = Math.ceil(checkedIndexes[0] / 2);
  const newSelectedCardArray = selectedCardArray.map((card) => (card === selectedCard ? null : card));
  const newCurrentLayer = [...currentLayer];
  newCurrentLayer[selectedCardArrayIndex] = newSelectedCardArray;
  const newNextLayer = [...nextLayer];
  newNextLayer[destinationIndex] = [...newNextLayer[destinationIndex], selectedCard];

  return updateCardStacks(
    {
      ...state,
      status: {
        ...state.status,
        optimizedModeMatrix: {
          checkedIndexes,
          currentLayer: newCurrentLayer,
          nextLayer: newNextLayer,
        },
        shouldChangeRound: !showRatingScale,
      },
    },
    selectedCard,
  );
};

const setResultRating = (results: QuickRankGameResults, card: CardType, rating: number) => {
  const newResults = { ...results };
  const newAttributeResult = { ...newResults[card.attribute.id] };
  newAttributeResult.rating = rating;
  newResults[card.attribute.id] = newAttributeResult;
  return newResults;
};

const rateCard = (state: QuickRankStateOptimizedMode, ratingScore: number) => {
  const {
    cards: {
      [QuickRankRenderableDestinations.RATED]: [ratedCard],
      [QuickRankNonRenderableDestinations.TO_RATE]: toRate,
    },
    results,
  } = state;

  if (!ratedCard) {
    throw new Error("No card to rate");
  }

  const newResults = setResultRating(results, ratedCard, ratingScore);
  const newToRate = toRate.filter((card) => card !== ratedCard);

  return {
    ...state,
    cards: {
      ...state.cards,
      [QuickRankNonRenderableDestinations.TO_RATE]: newToRate,
      [QuickRankRenderableDestinations.RATED]: [],
      [QuickRankRenderableDestinations.HAND]: [null, null],
    },
    status: {
      ...state.status,
      shouldChangeRound: true,
    },
    results: newResults,
  };
};

export default {
  NEXT_ROUND: nextRound,
  SELECT_CARD: selectCard,
  RATE_CARD: rateCard,
} as QuickRankActionHandler;
